/**
 * HelpModal Component
 *
 * A modal displaying keyboard shortcuts and help information.
 * Shows a table of keyboard shortcuts with styled key visualizations.
 *
 * Features:
 * - Displays keyboard shortcuts in a table format
 * - Styled key visualization (looks like keyboard keys)
 * - Configurable shortcuts list
 * - Inherits accessibility from Modal component
 *
 * Ported from Vue HelpModal.vue
 *
 * @example
 * ```tsx
 * <HelpModal
 *   isOpen={showHelp}
 *   title="Keyboard Shortcuts"
 *   onClose={() => setShowHelp(false)}
 * />
 * ```
 */

import React from "react";
import { Modal } from "../Modal";
import { cx } from "@/utils/cx";
import type { HelpModalProps, KeyboardShortcut } from "./types";
import styles from "./HelpModal.module.css";

/**
 * Default keyboard shortcuts for the application
 */
const DEFAULT_SHORTCUTS: KeyboardShortcut[] = [
  { keys: ["?"], description: "Open help modal" },
  { keys: ["E"], description: "Enter edit mode" },
  { keys: ["Esc"], description: "Cancel current window" },
  { keys: ["Shift", "Click"], description: "Period selection in edit mode" },
];

export const HelpModal: React.FC<HelpModalProps> = ({
  isOpen,
  title = "Help",
  shortcuts = DEFAULT_SHORTCUTS,
  onClose,
  className = "",
  "data-testid": testId,
}) => {
  // Build class names
  const containerClasses = cx(styles.container, className);

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      size="md"
      data-testid={testId}
    >
      <div className={containerClasses}>
        <table
          className={styles.table}
          role="grid"
          aria-label="Keyboard shortcuts"
        >
          <thead>
            <tr>
              <th scope="col" className={styles.headerCell}>
                Keys
              </th>
              <th scope="col" className={styles.headerCell}>
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            {shortcuts.map((shortcut, index) => (
              <tr key={index} className={styles.row}>
                <td className={styles.keysCell}>
                  <div className={styles.keysWrapper}>
                    {shortcut.keys.map((key, keyIndex) => (
                      <React.Fragment key={keyIndex}>
                        {keyIndex > 0 && (
                          <span className={styles.separator}>+</span>
                        )}
                        <kbd className={styles.key}>{key}</kbd>
                      </React.Fragment>
                    ))}
                  </div>
                </td>
                <td className={styles.descriptionCell}>
                  {shortcut.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

HelpModal.displayName = "HelpModal";
